import React, { useState } from 'react';

import { Table } from '@backstage/core-components';
import { alertApiRef, useApi } from '@backstage/core-plugin-api';

import { StorageQueue, useQueuePermissions } from '../hooks';
import { ConfirmClearDialog } from '../ConfirmClearDialog';
import { useStorageQueue } from './useStorageQueue';
import { ClearIcon } from '../../icons';

interface CardStorageQueueProps {
  data?: StorageQueue[];
  name: string;
  namespace: string;
}

const tableStyle: React.CSSProperties = {
  minWidth: '0',
  width: '100%',
  height: 'calc(100% - 10px)',
};

const actionsCellStyle: React.CSSProperties = {
  padding: '0px 24px',
};

export const CardStorageQueue = ({
  data = [],
  name,
  namespace,
}: CardStorageQueueProps) => {
  const [openConfirmDialog, setOpenConfirmDialog] = useState(false);
  const [loading, setLoading] = useState(false);
  const [queue, setQueue] = useState<StorageQueue>();

  const { hasClearPermission, env } = useQueuePermissions();
  const { clearQueue } = useStorageQueue({ name, namespace });
  const alertApi = useApi(alertApiRef);

  const columns = [{ title: 'Name', field: 'name' }];

  const clearAction = (storageQueue: StorageQueue) => ({
    icon: () => <ClearIcon fontSize="small" />,
    tooltip: 'Clear queue',
    onClick: () => {
      setQueue(storageQueue);
      setOpenConfirmDialog(true);
    },
  });

  const actions = hasClearPermission ? [clearAction] : [];

  const handleClear = async () => {
    try {
      setLoading(true);

      await clearQueue(queue as StorageQueue);

      setOpenConfirmDialog(false);
      alertApi.post({
        message: `Queue ${queue?.name} was cleared with success!`,
        severity: 'success',
        display: 'transient',
      });
    } catch (e: any) {
      alertApi.post({
        message: `Error on cleaning storage queue: ${e?.message}`,
        severity: 'error',
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Table
        title="Queue"
        columns={columns}
        actions={actions}
        data={data}
        style={tableStyle}
        options={{
          search: true,
          paging: false,
          loadingType: 'linear',
          maxBodyHeight: 500,
          actionsCellStyle,
        }}
      />

      <ConfirmClearDialog
        open={openConfirmDialog}
        queue={queue?.name || ''}
        enviroment={env}
        loading={loading}
        handleClick={handleClear}
        handleClose={() => setOpenConfirmDialog(false)}
      />
    </>
  );
};
